import React, { useState, useRef } from 'react';
import './styles/SecurePortal.css';
import PortalHeader from './PortalHeader';
import api from '../api/axiosConfig';
import { FRONTEND_BASE_URL, API_ENDPOINTS } from '../config/urls';
import { useNavigate } from 'react-router-dom';
import { getCurrentUserEmail } from './authUtils';

// const API_BASE_URL = process.env.REACT_APP_FRONTEND_BASE_URL || 'https://d2txmk2ndc4jr3.cloudfront.net' || 'http://localhost:3000';

const SecurePortal = () => {
  const [uploadType, setUploadType] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [link, setLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ message: '', type: '' });

  const toastTimeoutRef = useRef(null);
  const navigate = useNavigate();

  const showToast = (message, type = 'info') => {
    clearTimeout(toastTimeoutRef.current);
    setToast({ message, type });

    toastTimeoutRef.current = setTimeout(() => {
      setToast({ message: '', type: '' });
    }, 3000);
  };

  const handleGenerateLink = async () => {
    if (!uploadType) {
      showToast('Please select an upload type', 'error');
      return;
    }

    if (!ticketId.trim()) {
      showToast('Please enter a valid Ticket ID', 'error');
      return;
    }

    setLoading(true);
    try {
      // Validate ticket with backend API
      const validityRes = await api.get(API_ENDPOINTS.TICKET_VALIDITY, {
        params: { ticketId }
      });
      const validityData = validityRes.data;
      if (!validityData) {
        showToast('Invalid or expired ticket. Please check your ticket ID.', 'error');
        setLoading(false);
        return;
      }
      // Generate a one-time token from backend, passing ticketId
      const tokenRes = await api.get(API_ENDPOINTS.TOKEN_GENERATE, { params: { ticketId } });
      const token = tokenRes.data;
      // Build the upload link with the token
      let uploadLink;
      if (uploadType === 'merchant') {
        uploadLink = `${FRONTEND_BASE_URL}/dispute?token=${encodeURIComponent(token)}`;
      } else {
        uploadLink = `${FRONTEND_BASE_URL}/upload?token=${encodeURIComponent(token)}`;
      }
      setLink(uploadLink);
      // Log link generation details with user email
      const userEmail = getCurrentUserEmail();
      console.log(`Link generated by user:`, {
        user: userEmail,
        ticketId,
        uploadType,
        link: uploadLink
      });
      showToast('Secure one-time link generated!', 'success');
    } catch (error) {
      showToast('Error generating link', 'error');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(link).then(
      () => showToast('Link copied to clipboard!', 'success'),
      () => showToast('Failed to copy link', 'error')
    );
  };

  const resetForm = () => {
    setUploadType('');
    setTicketId('');
    setLink('');
  };

  return (
    <div className="portal-wrapper">
      <PortalHeader />
      <div style={{ position: 'absolute', top: 20, right: 30, zIndex: 10 }}>
        <a href="/logout">
          <button className="logout-btn" style={{ padding: '0.5rem 1rem', borderRadius: 6, border: 'none', background: '#e53e3e', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
            Logout
          </button>
        </a>
      </div>
      <div className="secure-card">
        <h2 className="title">🔗 Generate Secure Link</h2>
        <p className="subtitle">Create a secure upload link for your customer</p>

        <div className="radio-group">
          <label>
            <input
              type="radio"
              name="uploadType"
              value="general"
              checked={uploadType === 'general'}
              onChange={(e) => setUploadType(e.target.value)}
            />
            General Document Upload
          </label>
          <label>
            <input
              type="radio"
              name="uploadType"
              value="merchant"
              checked={uploadType === 'merchant'}
              onChange={(e) => setUploadType(e.target.value)}
            />
            Merchant Dispute
          </label>
        </div>

        <div className="floating-label">
          <input
            type="text"
            value={ticketId}
            onChange={(e) => setTicketId(e.target.value)}
            required
            disabled={!uploadType}
            placeholder={uploadType ? '' : 'Select upload type first'}
          />
          <label className={ticketId ? 'active' : ''}>🎫 HubSpot Ticket ID</label>
        </div>

        <button className="generate-btn" onClick={handleGenerateLink} disabled={loading}>
          {loading ? <span className="btn-loader"></span> : 'Generate Link 🔑'}
        </button>

        {/* Show expiration info if link is generated */}
        {link && (
          <>
            <div className="generated-link-block">
              <p className="success-msg">✅ Secure Link Generated!</p>
              <div className="link-box">
                <a href={link} target="_blank" rel="noopener noreferrer">
                  {link}
                </a>
                <button onClick={copyToClipboard} className="copy-icon" title="Copy Link">
                  <i className="fas fa-copy"></i>
                </button>
              </div>
              <p className="expiration-msg">This link will expire in 7 Days.</p>
            </div>
            <div className="action-buttons">
              <button onClick={resetForm} className="secondary-btn">
                <i className="fas fa-redo-alt"></i> Create Another
              </button>
            </div>
          </>
        )}
      </div>

      <div className="info-cards-wrapper">
        <div className="info-card">
          <div className="info-icon">🛡️</div>
          <h3>Secure & Encrypted</h3>
          <p>All documents are transferred with end-to-end encryption</p>
        </div>
        <div className="info-card">
          <div className="info-icon">🧾</div>
          <h3>Upload Options</h3>
          <p>Select appropriate type: General or Merchant Dispute</p>
        </div>
      </div>

      {toast.message && (
        <div className={`toast ${toast.type} show`}>
          <i
            className={`toast-icon fas ${
              toast.type === 'success'
                ? 'fa-check-circle'
                : toast.type === 'error'
                ? 'fa-exclamation-circle'
                : 'fa-info-circle'
            }`}
          ></i>
          <span className="toast-message">{toast.message}</span>
        </div>
      )}
    </div>
  );
};

export default SecurePortal;
